/**
 * Lab Reports API
 *
 * Strategy:
 *  1. Upload file bytes to POST /Binary (raw binary body, file MIME type as Content-Type).
 *     Returns Binary/{id} which is the permanent storage reference.
 *  2. Create a DocumentReference with attachment.url = "Binary/{id}" so the file
 *     reference is persisted in FHIR and survives page refreshes.
 *  3. For preview, fetch GET /Binary/{id} with auth and convert to a blob URL.
 *  4. Blob URLs are cached in memory per tab session to avoid redundant fetches.
 */

import { useAuthStore } from '@/lib/stores/authStore';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('labReports');

// LOINC code for Laboratory report
const LAB_REPORT_LOINC = '11502-2';
const LAB_REPORT_TYPE_TOKEN = `http://loinc.org|${LAB_REPORT_LOINC}`;

/**
 * Module-level cache: FHIR DocumentReference ID → blob URL.
 * Avoids re-fetching Binary content on every render within the same tab session.
 */
const blobUrlCache = new Map<string, string>();

/** Set a blob URL in the cache. Never revokes — URLs may be open in other tabs. */
function cacheBlobUrl(docRefId: string, blobUrl: string): void {
  blobUrlCache.set(docRefId, blobUrl);
}

/** Return a previously cached blob URL, or undefined. */
export function getCachedBlobUrl(docRefId: string): string | undefined {
  return blobUrlCache.get(docRefId);
}

// ─── FHIR base URL helper ──────────────────────────────────────────────────────

function getFhirBaseUrl(): string {
  const base = import.meta.env.VITE_AUTH_API_URL as string;
  return `${base}/api/hmis/fhir/R4`;
}

function getAuthHeaders(): Record<string, string> {
  const { token } = useAuthStore.getState();
  if (!token) return {};
  return { Authorization: token.startsWith('Bearer') ? token : `Bearer ${token}` };
}

// ─── Public types ─────────────────────────────────────────────────────────────

export interface LabReportRecord {
  id: string;
  fileName: string;
  uploadedAt: string;
  /** File extension uppercased, e.g. "PDF", "PNG" */
  fileType: string;
  contentType: string;
  /**
   * Blob URL for preview/download within the current session.
   * Only set for records whose binary content has been fetched or just uploaded.
   */
  url?: string;
  /** FHIR Binary reference, e.g. "Binary/abc123". Used for lazy on-demand fetching. */
  binaryRef?: string;
}

// ─── Internal FHIR types ──────────────────────────────────────────────────────

interface FhirBundle {
  entry?: Array<{ resource?: FhirDocumentReference }>;
}

interface FhirDocumentReference {
  id?: string;
  date?: string;
  content?: Array<{
    attachment?: {
      title?: string;
      contentType?: string;
      /** Relative FHIR reference, e.g. "Binary/abc123" */
      url?: string;
    };
  }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toPatientRef(patientId: string): string {
  return patientId.includes('/') ? patientId : `Patient/${patientId}`;
}

/** Extract file extension from a filename, uppercased. */
function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toUpperCase() || 'FILE';
}

/** Normalize blob MIME type and cache the resulting object URL. */
function createCachedBlobUrl(rawBlob: Blob, contentType: string, docRefId: string): string {
  const blob = rawBlob.type === contentType ? rawBlob : new Blob([rawBlob], { type: contentType });
  const blobUrl = URL.createObjectURL(blob);
  cacheBlobUrl(docRefId, blobUrl);
  return blobUrl;
}

// ─── Binary upload helper ─────────────────────────────────────────────────────

/**
 * Upload raw file bytes to POST /Binary.
 * Uses raw fetch so we can set the file's MIME type as Content-Type
 * (apiClient always sends application/fhir+json).
 */
async function uploadBinary(
  file: File,
  patientId: string,
  encounterId?: string,
  _appointmentId?: string
): Promise<string> {
  const rawPatientId = patientId.includes('/') ? patientId.split('/').pop()! : patientId;
  // Patient ID is sent as a query parameter — no sensitive data in headers
  const url = `${getFhirBaseUrl()}/Binary?patientId=${encodeURIComponent(rawPatientId)}`;
  const headers: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
    ...getAuthHeaders(),
  };

  // DIAGNOSTIC LOGGING — trace the Binary upload request
  logger.log('[uploadBinary] POST', url);
  logger.log('[uploadBinary] file:', file.name, '| size:', file.size, '| type:', file.type);
  logger.log('[uploadBinary] patientId:', rawPatientId, '| encounterId:', encounterId);
  logger.log('[uploadBinary] has auth header:', !!headers['Authorization']);

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: file,
  });

  logger.log('[uploadBinary] response status:', response.status, response.statusText);

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    logger.error('[uploadBinary] FAILED:', response.status, text);
    throw new Error(`Binary upload failed (${response.status}): ${text}`);
  }

  const data = (await response.json()) as { id?: string };
  logger.log('[uploadBinary] Binary created, id:', data?.id);
  if (!data?.id) throw new Error('Binary created but no ID returned');
  return data.id;
}

/**
 * Fetch Binary content and return a blob URL for preview/download.
 * Result is cached so subsequent calls for the same ID are instant.
 */
export async function fetchBinaryAsBlob(
  binaryId: string,
  docRefId: string,
  contentType: string
): Promise<string> {
  const cached = blobUrlCache.get(docRefId);
  if (cached) return cached;

  const baseUrl = getFhirBaseUrl();
  const authHeaders = getAuthHeaders();

  // Strategy 1: Request raw content directly with Accept header matching file type
  const rawUrl = `${baseUrl}/Binary/${binaryId}`;
  const rawResponse = await fetch(rawUrl, {
    headers: { Accept: contentType, ...authHeaders },
  });

  if (rawResponse.ok) {
    const responseContentType = rawResponse.headers.get('content-type') || '';
    logger.log('Binary response content-type:', responseContentType, 'expected:', contentType);

    // If we got actual binary content (not JSON), use it directly
    if (!responseContentType.includes('json') && !responseContentType.includes('fhir')) {
      const rawBlob = await rawResponse.blob();
      if (rawBlob.size > 100) {
        logger.log('Got raw binary content, size:', rawBlob.size);
        return createCachedBlobUrl(rawBlob, contentType, docRefId);
      }
    }

    // Server returned JSON — parse and extract content
    const data = await rawResponse.json();
    logger.log(
      'Binary JSON response keys:',
      Object.keys(data),
      'resourceType:',
      data?.resourceType
    );

    // Case 1: base64 data inline
    if (data?.data) {
      logger.log('Decoding base64 data, length:', data.data.length);
      const byteChars = atob(data.data);
      const byteNumbers = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteNumbers], { type: data.contentType || contentType });
      const blobUrl = URL.createObjectURL(blob);
      cacheBlobUrl(docRefId, blobUrl);
      return blobUrl;
    }

    // Case 2: presigned URL (GCS/S3)
    if (data?.url) {
      logger.log('Fetching from presigned URL:', data.url.substring(0, 80));
      const presignedRes = await fetch(data.url);
      if (presignedRes.ok) {
        const rawBlob = await presignedRes.blob();
        return createCachedBlobUrl(rawBlob, contentType, docRefId);
      }
    }

    // Case 3: Check meta.versionId or securityContext for GCS reference
    if (data?.securityContext?.reference) {
      logger.log('Found securityContext reference:', data.securityContext.reference);
    }
  }

  // Strategy 2: Try /fhir/R4/Binary/{id}/$binary (Medplum-specific raw content endpoint)
  try {
    const binaryEndpoint = `${baseUrl}/Binary/${binaryId}/$binary`;
    logger.log('Trying $binary endpoint');
    const res2 = await fetch(binaryEndpoint, {
      headers: { ...authHeaders },
    });
    if (res2.ok) {
      const rawBlob = await res2.blob();
      if (rawBlob.size > 100) {
        logger.log('Got content from $binary, size:', rawBlob.size);
        return createCachedBlobUrl(rawBlob, contentType, docRefId);
      }
    }
  } catch (e) {
    logger.warn('$binary endpoint failed:', e);
  }

  // Strategy 3: Try fetching via the HMIS API proxy (non-FHIR path)
  try {
    const hmisBase = (import.meta.env.VITE_AUTH_API_URL as string) + '/api/hmis';
    const res3 = await fetch(`${hmisBase}/binary/${binaryId}/content`, {
      headers: { Accept: contentType, ...authHeaders },
    });
    if (res3.ok) {
      const rawBlob = await res3.blob();
      if (rawBlob.size > 100) {
        logger.log('Got content from HMIS binary endpoint, size:', rawBlob.size);
        return createCachedBlobUrl(rawBlob, contentType, docRefId);
      }
    }
  } catch (e) {
    logger.warn('HMIS binary endpoint failed:', e);
  }

  throw new Error(
    `Unable to fetch Binary content for ${binaryId}. Check console logs for details.`
  );
}

// ─── Upload ───────────────────────────────────────────────────────────────────

/**
 * Upload a lab report for a patient.
 *
 * Attempts to upload file bytes to /Binary first (permanent storage).
 * If the Binary endpoint is unavailable (4xx/5xx), gracefully falls back to
 * metadata-only DocumentReference — the file is still cached as a blob URL
 * for preview within the current session.
 */
export async function uploadLabReport(
  file: File,
  patientId: string,
  encounterId?: string,
  appointmentId?: string,
  serviceRequestId?: string
): Promise<LabReportRecord> {
  logger.log(
    'Uploading lab report:',
    file.name,
    'for patient:',
    patientId,
    'encounter:',
    encounterId,
    'appointment:',
    appointmentId
  );

  const now = new Date().toISOString();

  // Step 1: Upload Binary (required — fail the whole upload if this fails)
  let binaryId: string;
  try {
    binaryId = await uploadBinary(file, patientId, encounterId, appointmentId);
    logger.log('[uploadLabReport] Step 1 OK — Binary created:', binaryId);
  } catch (err) {
    logger.error('[uploadLabReport] Step 1 FAILED — Binary upload:', err);
    throw new Error(`File upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }

  // Step 2: Create DocumentReference with Binary reference (using raw fetch to avoid auth cascade)
  logger.log('[uploadLabReport] Step 2 — Creating DocumentReference with Binary/' + binaryId);
  const attachment: Record<string, string> = {
    contentType: file.type || 'application/octet-stream',
    title: file.name,
    creation: now,
    url: `Binary/${binaryId}`,
  };

  const docRefBody = {
    resourceType: 'DocumentReference',
    status: 'current',
    type: {
      coding: [
        {
          system: 'http://loinc.org',
          code: LAB_REPORT_LOINC,
          display: 'Laboratory report',
        },
      ],
    },
    subject: {
      reference: toPatientRef(patientId),
    },
    date: now,
    content: [{ attachment }],
    ...((encounterId || appointmentId || serviceRequestId) && {
      context: {
        ...(encounterId && { encounter: [{ reference: `Encounter/${encounterId}` }] }),
        ...((appointmentId || serviceRequestId) && {
          related: [
            ...(appointmentId ? [{ reference: `Appointment/${appointmentId}` }] : []),
            ...(serviceRequestId ? [{ reference: `ServiceRequest/${serviceRequestId}` }] : []),
          ],
        }),
      },
    }),
  };

  const docRefUrl = `${getFhirBaseUrl()}/DocumentReference`;
  logger.log('[uploadLabReport] POST DocumentReference to:', docRefUrl);
  logger.log('[uploadLabReport] docRefBody type code:', LAB_REPORT_LOINC, '(Laboratory report)');
  const docRefResponse = await fetch(docRefUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/fhir+json',
      ...getAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(docRefBody),
  });

  logger.log(
    '[uploadLabReport] DocumentReference response:',
    docRefResponse.status,
    docRefResponse.statusText
  );

  if (!docRefResponse.ok) {
    const text = await docRefResponse.text().catch(() => docRefResponse.statusText);
    logger.error(
      '[uploadLabReport] Step 2 FAILED — DocumentReference creation:',
      docRefResponse.status,
      text
    );
    throw new Error(`DocumentReference creation failed (${docRefResponse.status}): ${text}`);
  }

  const docRefData = (await docRefResponse.json()) as { id?: string };
  const docRefId = docRefData?.id;
  logger.log('[uploadLabReport] Step 2 OK — DocumentReference created:', docRefId);
  if (!docRefId) throw new Error('DocumentReference created but no ID returned');

  // Step 3: Cache blob URL for immediate in-session preview
  const blobUrl = URL.createObjectURL(file);
  cacheBlobUrl(docRefId, blobUrl);

  return {
    id: docRefId,
    fileName: file.name,
    uploadedAt: now,
    fileType: getFileExtension(file.name),
    contentType: file.type || 'application/octet-stream',
    url: blobUrl,
    binaryRef: `Binary/${binaryId}`,
  };
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

/**
 * Fetch all lab report metadata for a patient from FHIR.
 * For records uploaded in this session, blob URLs come from the cache.
 * For older records, Binary content is fetched on-demand to create a blob URL.
 */
export async function fetchLabReports(patientId: string): Promise<LabReportRecord[]> {
  const patientRef = toPatientRef(patientId);
  logger.log('Fetching lab reports for patient:', patientRef);

  const params = new URLSearchParams();
  params.set('subject', patientRef);
  params.set('type', LAB_REPORT_TYPE_TOKEN);
  params.set('_sort', '-date');
  params.set('_count', '50');

  const url = `${getFhirBaseUrl()}/DocumentReference?${params.toString()}`;
  const res = await fetch(url, {
    headers: { ...getAuthHeaders() },
    credentials: 'include',
  });

  if (!res.ok) {
    logger.warn('Failed to fetch lab reports:', res.status);
    return [];
  }

  const responseData = (await res.json()) as FhirBundle;
  const entries = responseData?.entry || [];
  logger.log(`Found ${entries.length} lab reports`);

  const records: LabReportRecord[] = [];
  for (const entry of entries) {
    const resource = entry.resource;
    if (!resource?.id) continue;

    const attachment = resource.content?.[0]?.attachment;
    const contentType = attachment?.contentType || 'application/octet-stream';
    const rawBinaryUrl = attachment?.url; // May be "Binary/abc123" or a signed GCS URL

    // Normalise to "Binary/<uuid>" — Medplum's admin SDK resolves Binary references to
    // signed GCS URLs when serialising resources. loadReportUrl requires the Binary/<id> form.
    let binaryRef: string | undefined;
    if (rawBinaryUrl?.startsWith('Binary/')) {
      binaryRef = rawBinaryUrl;
    } else if (rawBinaryUrl) {
      const match = rawBinaryUrl.match(/\/binary\/([0-9a-f-]{36})/i);
      if (match) binaryRef = `Binary/${match[1]}`;
    }

    records.push({
      id: resource.id,
      fileName: attachment?.title || 'Untitled',
      uploadedAt: resource.date || '',
      fileType: getFileExtension(attachment?.title || ''),
      contentType,
      // Use cached blob URL if available; otherwise leave undefined for lazy loading
      url: blobUrlCache.get(resource.id),
      binaryRef,
    });
  }

  return records;
}

// ─── Delete ───────────────────────────────────────────────────────────────────

/**
 * Delete a lab report (DocumentReference) and its associated Binary.
 * First fetches the DocumentReference to find the Binary reference,
 * then deletes both resources.
 */
export async function deleteLabReport(docRefId: string): Promise<void> {
  logger.log('Deleting lab report:', docRefId);

  // Step 1: Fetch the DocumentReference to get the Binary reference
  const docRefUrl = `${getFhirBaseUrl()}/DocumentReference/${docRefId}`;
  const docRefRes = await fetch(docRefUrl, {
    headers: { ...getAuthHeaders() },
    credentials: 'include',
  });

  let binaryId: string | undefined;
  if (docRefRes.ok) {
    const docRef = (await docRefRes.json()) as FhirDocumentReference;
    const binaryRef = docRef.content?.[0]?.attachment?.url;
    if (binaryRef?.startsWith('Binary/')) {
      binaryId = binaryRef.split('/')[1];
    }
  }

  // Step 2: Delete the DocumentReference
  const deleteDocRefRes = await fetch(docRefUrl, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() },
    credentials: 'include',
  });

  if (!deleteDocRefRes.ok && deleteDocRefRes.status !== 404) {
    const text = await deleteDocRefRes.text().catch(() => deleteDocRefRes.statusText);
    throw new Error(`Failed to delete DocumentReference (${deleteDocRefRes.status}): ${text}`);
  }

  // Step 3: Delete the Binary (best-effort — don't fail if this errors)
  if (binaryId) {
    const binaryUrl = `${getFhirBaseUrl()}/Binary/${binaryId}`;
    await fetch(binaryUrl, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
      credentials: 'include',
    }).catch((err) => logger.warn('Failed to delete Binary:', err));
  }

  // Step 4: Revoke cached blob URL if any
  const cachedUrl = blobUrlCache.get(docRefId);
  if (cachedUrl) {
    URL.revokeObjectURL(cachedUrl);
    blobUrlCache.delete(docRefId);
  }

  logger.log('Lab report deleted:', docRefId);
}
